import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode.react';
import { api } from '../lib/api';
import { useWebSocket } from '../hooks/useWebSocket';

export const SenderView = ({ sessionId: propSessionId, sessionData: propSessionData, onSessionCreated }) => {
  const [senderName, setSenderName] = useState('');
  const [password, setPassword] = useState('');
  const [files, setFiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState(propSessionData);
  const [sessionId, setSessionId] = useState(propSessionId);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [downloadStats, setDownloadStats] = useState({});
  const [failedDownloads, setFailedDownloads] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [shareMode, setShareMode] = useState('global'); // 'global' or 'local'
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Automatic Network Detection
  useEffect(() => {
    const updateMode = () => {
      // If we have internet, prefer Global. If offline, switch to Local.
      if (navigator.onLine) {
        setShareMode('global');
      } else {
        setShareMode('local');
      }
    };

    // Initial check
    updateMode();

    // Event listeners for seamless switching
    window.addEventListener('online', updateMode);
    window.addEventListener('offline', updateMode);

    return () => {
      window.removeEventListener('online', updateMode);
      window.removeEventListener('offline', updateMode);
    };
  }, []);

  const uploadStartRef = useRef(null);
  const lastLoadedRef = useRef(0);
  const streamPausedRef = useRef({});
  const lastAckedChunksRef = useRef({});
  const lastAckTimeRef = useRef({});
  const [pausedUploads, setPausedUploads] = useState({});

  const sendMessageRef = useRef(null);
  const sharedFilesRef = useRef({});

  const streamFileViaSocketInternal = async (fileId, file) => {
    uploadStartRef.current = Date.now();
    lastLoadedRef.current = 0;

    const chunkSize = 64 * 1024; // 64KB chunks
    const totalChunks = Math.ceil(file.size / chunkSize);

    const fullPath = file.customPath || file.webkitRelativePath || file.name;
    const mimeType = file.type || 'application/octet-stream';

    // Reset flow control states
    lastAckedChunksRef.current[fileId] = -1;
    lastAckTimeRef.current[fileId] = Date.now();
    streamPausedRef.current[fileId] = false;

    if (sendMessageRef.current) {
      sendMessageRef.current({
        type: 'announce_file',
        fileId,
        fileName: fullPath,
        fileSize: file.size,
        fileType: mimeType
      });
    }

    const reader = new FileReader();
    let chunkIndex = 0;

    await new Promise((resolve, reject) => {
      const readNext = () => {
        if (streamPausedRef.current[fileId]) {
          setTimeout(readNext, 250);
          return;
        }

        const lastAck = lastAckedChunksRef.current[fileId] !== undefined ? lastAckedChunksRef.current[fileId] : -1;
        const lastAckTime = lastAckTimeRef.current[fileId] || Date.now();
        if (chunkIndex - lastAck > 50) {
          if (Date.now() - lastAckTime > 3000) {
            console.warn('Flow control ack timeout, resuming...');
            lastAckedChunksRef.current[fileId] = chunkIndex - 25;
            lastAckTimeRef.current[fileId] = Date.now();
          }
          setTimeout(readNext, 15);
          return;
        }

        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        reader.readAsDataURL(file.slice(start, end));
      };

      reader.onload = (e) => {
        if (sendMessageRef.current) {
          sendMessageRef.current({
            type: 'broadcast_file_chunk',
            chunk: e.target.result,
            fileId,
            fileName: fullPath,
            fileSize: file.size,
            fileType: mimeType,
            chunkIndex,
            totalChunks,
            isLast: chunkIndex === totalChunks - 1
          });
        }

        chunkIndex++;
        setUploadProgress(Math.round((chunkIndex / totalChunks) * 100));

        const now = Date.now();
        const duration = (now - uploadStartRef.current) / 1000;
        const loaded = chunkIndex * chunkSize;

        if (duration > 0) {
          const speed = loaded / duration;
          setUploadSpeed(speed);

          if (speed > 0) {
            const remaining = (file.size - loaded) / speed;
            setTimeRemaining(Math.round(remaining));
          }
        }

        if (chunkIndex < totalChunks) {
          setTimeout(readNext, 1);
        } else {
          resolve();
        }
      };
      reader.onerror = reject;
      readNext();
    });
  };

  const { isConnected, clientInfo, sendMessage } = useWebSocket(sessionId, (latestMessage) => {
    switch (latestMessage.type) {
      case 'request_file': {
        const { fileId, from: receiverId } = latestMessage;
        if (fileId) {
          const file = sharedFilesRef.current[fileId];
          if (file) {
            console.log(`Re-streaming file ${file.name} to receiver ${receiverId}`);
            setIsUploading(true);
            streamFileViaSocketInternal(fileId, file)
              .catch(err => {
                console.error('Error re-streaming file:', err);
                setError('Failed to re-stream file: ' + err.message);
              })
              .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
                setUploadSpeed(0);
                setTimeRemaining(null);
              });
          }
        }
        break;
      }
      case 'client_connected': {
        // Re-announce all shared files to update the ownerId/presence on the server
        Object.entries(sharedFilesRef.current).forEach(([fileId, file]) => {
          const fullPath = file.customPath || file.webkitRelativePath || file.name;
          const mimeType = file.type || 'application/octet-stream';
          if (sendMessageRef.current) {
            sendMessageRef.current({
              type: 'announce_file',
              fileId,
              fileName: fullPath,
              fileSize: file.size,
              fileType: mimeType
            });
          }
        });
        break;
      }
      case 'file_chunk_ack':
        if (latestMessage.fileId) {
          lastAckedChunksRef.current[latestMessage.fileId] = latestMessage.chunkIndex;
          lastAckTimeRef.current[latestMessage.fileId] = Date.now();
        }
        break;
      case 'transfer_pause':
        if (latestMessage.fileId) {
          streamPausedRef.current[latestMessage.fileId] = true;
          setPausedUploads(prev => ({ ...prev, [latestMessage.fileId]: true }));
        }
        break;
      case 'transfer_resume':
        if (latestMessage.fileId) {
          streamPausedRef.current[latestMessage.fileId] = false;
          setPausedUploads(prev => ({ ...prev, [latestMessage.fileId]: false }));
        }
        break;
      case 'download_stats':
        setDownloadStats(prev => ({
          ...prev,
          [latestMessage.fileId]: latestMessage.stats
        }));
        break;
      case 'download_failed':
        setFailedDownloads(prev => [...prev, {
          fileId: latestMessage.fileId,
          error: latestMessage.error,
          timestamp: Date.now()
        }]);
        break;
    }
  });

  sendMessageRef.current = sendMessage;


  const handleCreateSession = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');

    try {
      const response = await api.createSession({
        senderName: senderName || 'Anonymous',
        password: password || ''
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      setSessionId(response.sessionId);
      setSessionData(response);
      onSessionCreated(response);
    } catch (error) {
      setError(error.message || 'Failed to create session');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const traverseFileTree = async (item, path = '') => {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file(file => {
          // Attach the relative path so we can preserve folder structure
          Object.defineProperty(file, 'customPath', {
            value: path + file.name,
            writable: true,
            enumerable: true
          });
          resolve([file]);
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        const files = [];
        const readEntries = () => {
          dirReader.readEntries(async (entries) => {
            if (entries.length === 0) {
              resolve(files);
            } else {
              for (const entry of entries) {
                const subFiles = await traverseFileTree(entry, path + item.name + '/');
                files.push(...subFiles);
              }
              readEntries();
            }
          });
        };
        readEntries();
      } else {
        resolve([]);
      }
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    let newFiles = [];

    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry ? items[i].webkitGetAsEntry() : null;
        if (item) {
          const extractedFiles = await traverseFileTree(item);
          newFiles = [...newFiles, ...extractedFiles];
        } else if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          if (file) newFiles.push(file);
        }
      }
    } else {
      newFiles = Array.from(e.dataTransfer.files);
    }

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleUpload = async () => {
    if (files.length === 0 || !sessionId) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadSpeed(0);
    setTimeRemaining(null);
    setError('');

    try {
      // 1. Process Files via WebSocket Streaming (No Disk Usage)
      for (const file of files) {
        const fileId = window.crypto.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).substring(2);
        sharedFilesRef.current[fileId] = file;

        await streamFileViaSocketInternal(fileId, file);

        setUploadedFiles(prev => [...prev, {
          id: fileId, name: file.name, size: file.size, isStreamed: true
        }]);
      }

      setFiles([]);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 5000);
    } catch (error) {
      setError(error.message || 'Failed to sync files');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadSpeed(0);
      setTimeRemaining(null);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec < 1024) return `${Math.round(bytesPerSec)} B/s`;
    if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '';
    if (seconds < 60) return `${seconds}s remaining`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s remaining`;
  };

  if (!sessionId) {
    return (
      <div className="theme-green">
        <div className="max-w-xl mx-auto py-10 px-4 animate-fade-in-up">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4" style={{background:'var(--primary-50)'}}>📡</div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 tracking-tight" style={{color: 'var(--text-primary)'}}>Initialize Broadcast</h1>
          <p className="text-sm" style={{color: 'var(--text-secondary)'}}>Start sharing files instantly with Universal Network Support</p>
        </div>

        <div className="card p-6 sm:p-8" style={{boxShadow:'var(--shadow-lg)'}}>
          <form onSubmit={handleCreateSession} className="space-y-6">
            <div>
              <label htmlFor="senderName" className="label">Broadcast Name</label>
              <input
                type="text"
                id="senderName"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="e.g. Design Team Share"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">Secure Password (Optional)</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank for public access"
                className="input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3.5 flex items-center gap-3 animate-fade-in">
                <span className="text-red-500 text-sm">⚠️</span>
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isCreating}
              className="btn-primary w-full py-3.5 text-base"
            >
              {isCreating ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Creating...</>
              ) : 'Launch Broadcast →'}
            </button>
          </form>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div className={shareMode === 'local' ? 'theme-orange' : 'theme-green'}>
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6">
      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left Column: Session Controls */}
        <div className="lg:col-span-5 space-y-8">
          {/* Dedicated Session ID Section */}
          <div className="bg-gradient-to-br from-[var(--primary)] to-[var(--primary-light)] rounded-3xl p-6 sm:p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70">Connection Port</h3>
                <div className="flex bg-black/20 px-4 py-2 rounded-xl backdrop-blur-md">
                  <span className="text-[10px] font-black tracking-widest text-white flex items-center gap-2">
                    {shareMode === 'global' ? (
                      <><span className="w-2 h-2 bg-blue-400 rounded-full"></span> GLOBAL MODE</>
                    ) : (
                      <><span className="w-2 h-2 bg-orange-400 rounded-full"></span> LOCAL (LAN) MODE</>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between mb-8">
                <div>
                  <span className="text-[10px] font-medium opacity-60 block mb-1">SESSION ID</span>
                  <p className="text-3xl font-mono font-black tracking-tighter">{sessionId}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-[10px] font-medium opacity-60 block mb-1">NETWORK</span>
                    <div className="flex items-center gap-2 justify-end">
                      <span className={`h-2 w-2 rounded-full ${shareMode === 'global' ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]' : 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.5)]'}`}></span>
                      <span className="text-[10px] font-bold">{shareMode === 'global' ? 'ONLINE' : 'OFFLINE'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-black/20 rounded-2xl p-4 backdrop-blur-sm border border-white/5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[9px] font-black uppercase tracking-widest opacity-70">
                      {shareMode === 'global' ? '🌐 INTERNET LINK' : '🏠 LAN LINK (ULTRA FAST)'}
                    </span>
                    <button
                      onClick={() => {
                        const link = shareMode === 'global' ? `https://heksta.in/join/${sessionId}` : sessionData?.localJoinLink;
                        navigator.clipboard.writeText(link);
                      }}
                      className="text-[9px] font-bold bg-white/10 hover:bg-white/20 px-2 py-1 rounded-md transition-all"
                    >COPY</button>
                  </div>
                  <p className="text-xs font-mono break-all opacity-80 leading-relaxed">
                    {shareMode === 'global' ? `https://heksta.in/join/${sessionId}` : sessionData?.localJoinLink}
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-5 flex flex-col items-center gap-4">
                  <QRCode
                    value={(shareMode === 'global' ? `https://heksta.in/join/${sessionId}` : sessionData?.localJoinLink) || ''}
                    size={140}
                    level="H"
                    includeMargin={false}
                    renderAs="svg"
                    className="w-full max-w-[160px] h-auto"
                  />
                  <p className="text-[10px] text-slate-400 font-bold text-center">
                    {shareMode === 'global' ? 'Scan to join via Internet' : 'Scan to join via Local Wi-Fi (No Data Used)'}
                  </p>
                </div>
              </div>
            </div>
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 h-64 w-64 bg-white/10 rounded-full blur-3xl"></div>
          </div>

          <div className="card p-5">
            <h3 className="section-title mb-4">Real-time Metrics</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="stat-card">
                <span className="text-[10px] font-semibold uppercase tracking-widest block mb-1" style={{color: 'var(--text-muted)'}}>Participants</span>
                <span className="text-xl font-bold" style={{color:'var(--primary)'}}>{clientInfo?.sessionInfo?.connectedClients || 0}</span>
              </div>
              <div className="stat-card">
                <span className="text-[10px] font-semibold uppercase tracking-widest block mb-1" style={{color: 'var(--text-muted)'}}>Files Shared</span>
                <span className="text-xl font-bold" style={{color:'var(--primary)'}}>{uploadedFiles.length}</span>
              </div>
            </div>

            <button onClick={() => window.location.href = '/'} className="btn-danger w-full mt-5 py-2.5 text-sm">
              Terminate Session
            </button>
          </div>
        </div>

        {/* Right Column: Upload & Files */}
        <div className="lg:col-span-7 space-y-8">
          <div className="card h-full flex flex-col">
            <h1 className="text-2xl font-bold text-slate-900 mb-8">Data Broadcast</h1>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-4 animate-shake">
                <span className="text-xl">⚠️</span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-700 mb-1">Upload Interrupted</p>
                  <p className="text-xs text-red-600 font-medium leading-relaxed">{error}</p>
                </div>
                <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 text-sm">Dismiss</button>
              </div>
            )}

            {/* Upload Area */}
            <div
              className={`relative group mb-8 ${isDragging ? 'opacity-80 scale-[0.98]' : ''} transition-all duration-300`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input type="file" multiple onChange={handleFileSelect} className="hidden" id="fileInput" />
              <input type="file" multiple webkitdirectory="true" directory="true" onChange={handleFileSelect} className="hidden" id="folderInput" />

              <div className={`border-2 border-dashed ${isDragging ? 'border-[var(--primary)] bg-[var(--primary-50)]' : 'border-slate-200 dark:border-slate-700'} rounded-2xl p-8 sm:p-10 text-center group-hover:border-[var(--primary-light)] group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/50 transition-all duration-200`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 transition-transform group-hover:scale-110" style={{background:'var(--primary-50)'}}>
                  <span className="text-2xl">⬆️</span>
                </div>
                <div className="text-base font-bold mb-1" style={{color: 'var(--text-primary)'}}>Select Data</div>
                <p className="text-sm mb-5" style={{color: 'var(--text-secondary)'}}>Drag files & folders here, or click to browse</p>
                <div className="flex gap-3 justify-center relative z-10">
                  <label htmlFor="fileInput" className="btn-secondary cursor-pointer text-xs py-2 px-4 m-0">Select Files</label>
                  <label htmlFor="folderInput" className="btn-secondary cursor-pointer text-xs py-2 px-4 m-0">Select Folder</label>
                </div>
              </div>
            </div>

            {/* Selected / Uploading Files */}
            {(files.length > 0 || isUploading) && (
              <div className="space-y-6 flex-1">
                <div className="flex justify-between items-center px-1">
                  <h3 className="font-bold text-slate-900">
                    {isUploading
                      ? (uploadProgress === 100 ? 'Finalizing Broadcast...' : 'Broadcasting Data...')
                      : 'Selected Payloads'}
                  </h3>
                  <span className="badge">{files.length} items</span>
                </div>

                <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-3">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-slate-100 p-4 rounded-2xl shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 bg-slate-100 rounded-xl flex items-center justify-center text-xl">📄</div>
                        <div>
                          <p className="text-sm font-bold text-slate-900 truncate max-w-[200px]" title={file.customPath || file.webkitRelativePath || file.name}>{file.customPath || file.webkitRelativePath || file.name}</p>
                          <p className="text-xs text-slate-500 font-medium">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      {!isUploading && (
                        <button onClick={() => handleRemoveFile(idx)} className="text-slate-400 hover:text-red-500 transition-colors">✕</button>
                      )}
                    </div>
                  ))}
                </div>

                {isUploading && (
                  <div className="rounded-2xl p-5 border" style={{background:'var(--primary-50)',borderColor:'color-mix(in srgb, var(--primary) 15%, transparent)'}}>
                    <div className="flex justify-between items-end mb-3">
                      <div>
                        <span className="section-title mb-1 block">Total Progress</span>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-black" style={{color:'var(--primary)'}}>{uploadProgress}%</span>
                          <span className="text-xs font-medium" style={{color: 'var(--text-secondary)'}}>{formatSpeed(uploadSpeed)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-medium block" style={{color: 'var(--text-muted)'}}>ETA</span>
                        <span className="text-sm font-bold" style={{color:'var(--primary)'}}>
                          {uploadProgress === 100 ? 'Finishing...' : (formatTime(timeRemaining) || '--')}
                        </span>
                      </div>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  </div>
                )}

                {uploadSuccess && (
                  <div className="bg-green-50 border border-green-100 rounded-3xl p-6 flex flex-col items-center text-center animate-bounce-in">
                    <div className="h-12 w-12 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl mb-3">✓</div>
                    <p className="text-green-800 font-bold">Broadcast Successful!</p>
                    <p className="text-green-600 text-xs mt-1">Files are now available for all participants</p>
                  </div>
                )}

                {!isUploading && (
                  <button onClick={handleUpload} className="btn-primary w-full py-5 text-xl mt-4">Broadcast to session</button>
                )}
              </div>
            )}

            {/* History Section if no active upload */}
            {!isUploading && uploadedFiles.length > 0 && (
              <div className="mt-8 pt-8 border-t border-slate-100">
                <h3 className="font-bold text-slate-900 mb-6">Broadcast History</h3>
                <div className="space-y-3">
                  {uploadedFiles.slice(-3).reverse().map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                      <div className="flex items-center gap-4">
                        <div className="text-green-500">✓</div>
                        <div>
                          <p className="text-sm font-bold text-slate-900 truncate max-w-[200px]" title={file.customPath || file.webkitRelativePath || file.name}>{file.customPath || file.webkitRelativePath || file.name}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};
